import { get, forEach, round } from 'lodash-es';
import { SITE_URL } from 'site-modules/shared/pages/seo';
import { getModelState } from 'client/data/luckdragon/model';
import { DealerInfoModel, getDealerInfoNamePath } from 'client/data/models/dealer-info';
import { DealerInventoryModel } from 'client/data/models/dealer-inventory';
import { SEOModel } from 'client/data/models/seo';
import { DEALER_REVIEWS_PER_PAGE, DEFAULT_REVIEWS_TYPE } from 'client/constants/dealer-reviews';
import { ReviewsModel, ReviewsPaths } from 'client/data/models/dealer-reviews';
import { time12To24HoursFormat } from 'client/utils/time-format';
import { getOverallRating } from 'client/site-modules/dealerships/utils/dealer-rating';

import { getDealerModelPath } from './dealer';

export function createOpeningHours(workHours) {
  const openingHours = [];
  forEach(workHours, (day, dayName) => {
    let dayNameShortcut = dayName.slice(0, 2);
    dayNameShortcut = dayNameShortcut.charAt(0).toUpperCase() + dayNameShortcut.slice(1);
    openingHours.push(`${dayNameShortcut} ${time12To24HoursFormat(day.open)}-${time12To24HoursFormat(day.close)}`);
  });
  return openingHours;
}

function buildPaginationLink({ seo, props, pageNumber, reviewType }) {
  const queryString =
    pageNumber > 1
      ? `?page=${pageNumber}${reviewType && reviewType !== DEFAULT_REVIEWS_TYPE ? `&type=${reviewType}` : ''}`
      : `${reviewType && reviewType !== DEFAULT_REVIEWS_TYPE ? `?type=${reviewType}` : ''}`;
  return seo.canonical ? `${seo.canonical}${queryString}` : `${SITE_URL}${props.location.pathname}${queryString}`;
}

export function createSEOContent(state, props) {
  const modelState = getModelState(state);
  const { logicalName } = props.match.params;
  const dealerInfo = modelState.get(getDealerInfoNamePath(logicalName), DealerInfoModel);

  const seo = {};
  if (!dealerInfo) {
    return seo;
  }

  const dealerInventory = modelState.get(
    getDealerModelPath('dealerInventory', { ...props, params: props.match.params }),
    DealerInventoryModel
  );
  const backlinks = modelState.get('pageProfile.backlinks', SEOModel);
  const { name, address, about } = dealerInfo;
  const { city, stateName, stateCode, street, zip } = address;
  const dealerInvCount = get(dealerInventory, 'rooftopCounters.total');

  seo.title = `${name} Car Dealership ${city} ${stateName} | Edmunds`;
  seo.description = `Research and Write Consumer Reviews of ${name} in ${city}, ${stateName} at Edmunds.com`;
  seo.keywords = `${name} ${city} ${stateName} ${zip} Research Consumer Reviews Car Dealers Dealerships Edmunds.com`;

  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: { '@id': 'https://www.edmunds.com/', name: 'Home' },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: { '@id': `https://www.edmunds.com${props.location.pathname}`, name },
      },
    ],
  };

  const autoDealer = {
    '@type': 'AutoDealer',
    '@id': `https://www.edmunds.com${props.location.pathname}dealer_info_container`,
    openingHours: createOpeningHours(dealerInfo.workHours),
    name: dealerInfo.name,
    image: dealerInfo.logoUrl,
    address: {
      '@type': 'PostalAddress',
      streetAddress: street,
      addressLocality: city,
      addressRegion: stateCode,
      postalCode: zip,
      telephone: dealerInfo.phones && dealerInfo.phones[0],
      url: dealerInfo.dealerWebSites && dealerInfo.dealerWebSites[0],
      addressCountry: {
        '@type': 'Country',
        name: 'USA',
      },
    },
  };

  const geoCoordinates = {
    '@type': 'GeoCoordinates',
    latitude: dealerInfo.location && dealerInfo.location.lat,
    longitude: dealerInfo.location && dealerInfo.location.lon,
  };

  const overallRatingByType = modelState.get(ReviewsPaths.getOverallRatings({ dealerId: dealerInfo.id }), ReviewsModel);
  const overallRating = getOverallRating(overallRatingByType);

  const { averageRating, totalCount } = overallRating;

  if (averageRating) {
    autoDealer.aggregateRating = {
      '@type': 'AggregateRating',
      '@id': `https://www.edmunds.com${props.location.pathname}dealer_ratings`,
      ratingValue: round(averageRating, 2),
      reviewCount: totalCount,
    };

    // build next/prev links
    const reviewFilters = modelState.get(ReviewsPaths.getReviewsFilterPath(), ReviewsModel);
    const page = get(reviewFilters, 'page');
    const type = get(reviewFilters, 'type');
    const pageNumber = parseInt(page || '1', 10);

    if (totalCount > pageNumber * DEALER_REVIEWS_PER_PAGE) {
      seo.next = buildPaginationLink({ seo, props, pageNumber: pageNumber + 1, reviewType: type });
    }
    if (pageNumber > 1) {
      seo.prev = buildPaginationLink({ seo, props, pageNumber: pageNumber - 1, reviewType: type });
    }
  }

  const removeRobots = backlinks && (about || dealerInvCount || totalCount);

  if (!removeRobots) {
    seo.robots = { noIndex: true, noFollow: true };
  }

  seo.jsonld = [breadcrumbList, autoDealer, geoCoordinates];

  return seo;
}
